import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { Client, ISizeName } from '@df/core/service/client';
import { NgInject } from '@df/ng/ng-inject';

declare module '@df/catalog-ui/listing/product-listing' {
    interface ProductListing {
        plugin: {
            grid: GridPlugin;
        };
    }
}

declare module '@df/catalog/product/product' {
    interface IProductDataV2 {
        /**
         * Either a number then its number of cols it can take
         * or a string, then a notation like: 1,2,2,4 is expected with values for size S, M, L, X
         * or 1,2 for S,!S
         */
        colspan?: number | string;
        rowspan?: number | string;
    }
}

export interface IGridPluginOptions {
    /**
     * Allowed layouts, for example
     * 1x1 : ['S', 'M','X','L],
     * 2x1 : ['X,'L']
     *
     * key is: [colspan]x[rowspan]
     */
    layouts: { [layout: string]: ISizeName[] };
    /**
     * Gutter sizes
     */
    gutter?: {
        col: [number | string, number | string, number | string, number | string];
        row: [number | string, number | string, number | string, number | string];
    };
}

/**
 *
 *
 * Sample implementation
 *
 * <div [ngClass]="['listing-size-' + listing.plugin.grid.gridSize]"
 *     [grid]="listing.plugin.grid.gridSize">
 *    <product *ngFor="let model of listing.models; trackBy listing.modelTrackByFn"
 *             [model]='model'
 *             [ngClass]="['colspan-' + listing.plugin.grid.colspan(model), 'rowspan-' + listing.plugin.grid.rowspan(model)]"
 *             [gridColumn]="listing.plugin.grid.colspan(model)"
 *             [gridRowSpan]="listing.plugin.grid.rowspan(model)"
 *             [listing]='listing'></product>
 * </div>
 *
 * Then install plugin like this:
 *
 *  protected onListingChange() {
 *         new GridPlugin(this.listing);
 *         super.onListingChange();
 *  }
 */
export class GridPlugin<TListing extends ProductListing = ProductListing> {
    @NgInject(Client)
    protected client!: Client;

    constructor(
        protected listing: TListing,
        protected options?: IGridPluginOptions
    ) {
        // bind to listing
        if (!listing.plugin) {
            listing.plugin = {} as any;
        }
        listing.plugin.grid = this;

        // subscribe to events to patch limit when models or limites need to change
        listing.subscribe(() => this.patchLimit());
        this.patchLimit();
    }

    /**
     * Grid size
     */
    gridSize(): number {
        return this.listing.size?.getState(1) || 1;
    }

    /**
     * Gets gutter for row separation
     */
    getGutterRow() {
        return this.options?.gutter?.row[this.client.sizeId] || 0;
    }

    /**
     * Gets gutter for column separation
     */
    getGutterCol() {
        return this.options?.gutter?.col[this.client.sizeId] || 0;
    }

    /**
     * Gets pair of colspan and rowspan
     */
    protected getSpan(model: Product<IProductDataV2>) {
        const gridSize = this.gridSize();
        const colspan = this.convertSpan(model?.data?.colspan);
        const rowspan = this.convertSpan(model?.data?.rowspan);

        if (colspan > gridSize) {
            return [1, 1];
        }
        const result = [colspan, rowspan];
        // validate if layout is allowed
        const allowedLayouts = this.options?.layouts;
        if (allowedLayouts && !allowedLayouts[result.join('x')]?.includes(this.client.size)) {
            return [1, 1];
        }
        return result;
    }

    /**
     * Product colpsan
     */
    colspan(model: Product<IProductDataV2>) {
        return this.getSpan(model)[0];
    }

    /**
     * Product rowpspan
     */
    rowspan(model: Product<IProductDataV2>) {
        return this.getSpan(model)[1];
    }

    /**
     * Normalize span for current size area
     */
    protected convertSpan(span: string | number | undefined): number {
        switch (typeof span) {
            case 'undefined':
                return 1;
            case 'number':
                return span;
            case 'string':
                const spanPerSize = span.split(',');

                if (spanPerSize.length === 2) {
                    return Number(spanPerSize[this.client.isS ? 0 : 1]);
                }

                return Number(spanPerSize[this.client.sizeId]) || 1;
        }
        return 1;
    }

    /**
     * Update limit to align make sure that no empty spaces are left on the grid
     */
    private patchLimit() {
        const value = this.listing.getLimit();
        const models = this.listing.getFinalModels();
        const qty = models?.length || 0;

        if (qty && value < qty) {
            const grid = this.gridSize();
            if (grid > 1) {
                let fieldsTaken = 0;
                const minIndex = models.findIndex((model, index) => {
                    const span = this.getSpan(model);
                    fieldsTaken += span[0] * span[1];
                    return index + 1 >= value && !(fieldsTaken % grid);
                });
                // if match found align to it, otherwise show all as are at the end most likely
                this.listing.setLimit(minIndex === -1 ? qty : minIndex + 1);
            }
        }
    }

    getOverflowRowsQty(): number {
        const overflow = this.listing.modelsWithOverflow?.length - this.listing.models.length;
        if (overflow > 0) {
            const gridSize = this.gridSize();
            if (gridSize) {
                return Math.ceil(overflow / gridSize);
            }
        }
        return 0;
    }

    getImageHeight(ratio1x1: number, colspan: number, width: number) {
        const gutter = this.getGutterCol();
        const stringGutter = typeof gutter === 'string' ? gutter : `var(--grid-gutter-size, 5px) * ${this.getGutterCol()}`;
        return `calc((${width}px - ${stringGutter} * ${colspan - 1}) / ${colspan / ratio1x1})`;
    }
}
